// The dashboard's feature flags. An entry here is the flag's only definition;
// its state lives in kalos-core (FeatureFlagService) and changes without a
// deploy. A flag that is also checked in Go uses the same key in
// kalos-core's v2/pkg/featureflag/registry.go.
//
// Add an entry in the same PR as the code it gates, and delete it (moving the
// key to retired.ts) in the PR that removes the check. registry.test.ts holds
// the rules; vite.config.ts publishes this list as feature-flags.json, which
// kalos-core's daily sweep reads to tell which keys the deployed bundle still
// asks for.
//
// Keep this file free of imports: vite.config.ts loads it at build time.

export interface FlagDefinition {
  /** One plain sentence the flag's owner reads on the admin page. */
  description: string;
  /** ClickUp task IDs. The first created the flag; triage appends follow-ups. */
  tickets: readonly [string, ...string[]];
  /** Date the flag's PR was opened, YYYY-MM-DD. */
  createdAt: string;
  /** Days on before cleanup. Defaults to 14. */
  cleanupAfterDays?: number;
}

export const FEATURE_FLAGS = {
  'mobile.field-layout': {
    description:
      'The phone and tablet screens: the job record, its sheets and full-screen pages, and the tour; customer and property records, the tech dashboard, timesheets, expenses and the tool fund; and, as each one ships, Add Job, the scheduler, calendar, dispatch, directory, HR, payroll, billing, memberships, reports and analytics. Off keeps the desktop layout on every screen.',
    tickets: [
      '86bbkfqen',
      '86bbkfrtp',
      '86bbkftcv',
      '86bbkftw3',
      '86bbkfucm',
      '86bbkfuua',
      '86bbkfv7g',
      '86bbkfvkc',
      '86bbkfvwg',
      '86bc845j5',
      '86bcbhvk3',
      '86bcbhvr1',
      '86bcbhvry',
      '86bcbhvxv',
      '86bccea1j',
      '86bcdb9bp',
      '86bcdb9c9',
      '86bcdca31',
      '86bcdca47',
      '86bcddzm7',
      '86bcewjvt',
      '86bcg7mwv',
      '86bcgacum',
      '86bcgaene',
      '86bcgaua1',
      '86bcgbqn7',
      '86bcgcqen',
      '86bcge8y4',
      '86bcgg9dc',
      '86bcggxzj',
      '86bcgh9b5',
    ],
    createdAt: '2026-10-20',
  },
  'memberships.contract-amendments': {
    description:
      "Plans added to an approved residential contract get their own review and payment instead of re-opening the contract, and renewals are priced from today's catalog instead of copying the last term's invoice.",
    tickets: ['86bc8kzgt'],
    createdAt: '2026-10-21',
  },
  'job-details.tech-record-payment': {
    description:
      'Techs can record a payment they already collected on a closed-out job, from Add payment on the job and a Completed tab on their dashboard.',
    tickets: ['86bcj1v6x'],
    createdAt: '2026-10-23',
  },
  'project-schedule.module': {
    description:
      'Project Scheduling: the Project Scheduling page and its side menu entry, the My Schedule card on the dashboard, and Change requests in Lodging. Off hides all of them.',
    tickets: ['86b8f1gb2'],
    createdAt: '2026-10-23',
  },
  'job-forms.subtype-trade-groups': {
    description:
      'The Sub Type dropdown on Admin View, Field View, Add Job and Schedule Call lists Sub Types under HVAC, Plumbing, Electrical and Other headers. Off keeps one flat list.',
    tickets: ['86bcndkpm'],
    createdAt: '2026-10-23',
  },
  'tool-fund.admin-purchase': {
    description:
      "Tool fund admins record a purchase from Add Transaction with each tool's receipt and purchase date, and choose who pays when requesting for someone else. The Tool Log lists requests by who pays. Off keeps the plain transaction form and today's request forms.",
    tickets: ['86bcjnarn', '86bcdca31'],
    createdAt: '2026-10-21',
  },
  'performance-reviews.redesign': {
    description:
      'The new performance reviews for HR, managers and employees: the review page, the reviews list, automatic reviews and their settings, the dashboard card and the reminders.',
    tickets: ['86bcdmtz4', '86bcdmtx1', '86bcdmu1q', '86bcdmtwb', '86bcdmtzc'],
    createdAt: '2026-10-22',
    cleanupAfterDays: 30,
  },
  'org-chart.module': {
    description:
      "The organization chart built from teams, groups and job titles, where it reaches people who do not maintain it: the directory's team and supervisor filters. Off keeps those out of sight; the hand-maintained chart on the HR dashboard is unaffected either way. What its admins build the structure with is not behind this, being invisible to everyone else already — the chart page at /reports/employee-org-chart, the Manage Organization screen, and the job title and team fields on the employee record, which off left even its admin typing free text over a managed title.",
    tickets: ['86bcre23c'],
    createdAt: '2026-10-27',
  },
  'new-hire.job-title-picker': {
    description:
      "The new hire form picks its job title from the managed list instead of taking free text, and saves it as role_id so the employee created from the hire is linked to the title rather than to a copy of its name. Off keeps the plain field. Separate from org-chart.module because the people who file new hires are not the chart's admins.",
    tickets: ['86bcvkrua'],
    createdAt: '2026-10-28',
  },
  'qbo.ap-bill-sync': {
    description:
      'Accounts Payable admins can post vendor invoices from the Accounts Payable page straight to QuickBooks as Bills and Vendor Credits, marking them Processed. Off keeps only the preview, which shows what would post and changes nothing. kalos-core checks the same key before it posts.',
    tickets: ['86bcu7ju8'],
    createdAt: '2026-10-30',
  },
  'job-creation.multi-day': {
    description:
      'Add Job offers a Job length switch for commercial and residential jobs other than Maintenance: Multi-day swaps the time windows for a Date of Service and End Date. The Project form shows how many days the project spans. Off books single-day jobs only and hides the day count.',
    tickets: ['86bcpyqpn'],
    createdAt: '2026-10-27',
  },
} as const satisfies Record<string, FlagDefinition>;

export type FlagKey = keyof typeof FEATURE_FLAGS;

/** The registry as a plain record, for code that walks every entry. */
export const FLAG_DEFINITIONS: Readonly<Record<string, FlagDefinition>> = FEATURE_FLAGS;

/** `<area>.<feature>`, lowercase kebab-case on both sides. */
export const FLAG_KEY_PATTERN = /^[a-z0-9-]+\.[a-z0-9-]+$/;

/** feature_flag.flag_key's width. */
export const MAX_FLAG_KEY_LENGTH = 64;

export const DEFAULT_CLEANUP_AFTER_DAYS = 14;

export const isValidFlagKey = (key: string) =>
  key.length <= MAX_FLAG_KEY_LENGTH && FLAG_KEY_PATTERN.test(key);

export const cleanupDays = (def: Pick<FlagDefinition, 'cleanupAfterDays'>) =>
  def.cleanupAfterDays || DEFAULT_CLEANUP_AFTER_DAYS;
